import { useEffect, useMemo, useRef, useState } from "react";
import {
  ChatCircleText,
  PaperPlaneRight,
  Sparkle,
  X,
  Robot,
  User,
  ArrowClockwise,
  Trash,
  Microphone,
  Stop,
  SpeakerHigh,
  SpeakerSlash,
  CaretDown,
} from "@phosphor-icons/react";
import api, { API } from "../../lib/api";
import { toast } from "sonner";

const PERSONALITIES = [
  { key: "professional", label: "Profesional", emoji: "📊", description: "Neutral, con datos precisos" },
  { key: "strict", label: "Estricto", emoji: "⚡", description: "Directo, sin sugar-coating" },
  { key: "friendly", label: "Amigable", emoji: "🤝", description: "Cálido y motivador" },
  { key: "eli5", label: "Simple", emoji: "🎯", description: "Como si tuvieras 12 años" },
];

const QUICK_PROMPTS = [
  "¿Qué debo priorizar este mes?",
  "¿Puedo pagar mi Amex sin quebrarme?",
  "¿Cuánto debería ahorrar para retiro?",
  "Simula pagar $500 extra a mi peor deuda",
];

/**
 * Floating side panel with the CFO conversational chat.
 * Injects the user's real financial context into every request.
 */
export default function CFOChat({ open, onClose, scope, initialSessionId }) {
  const [personality, setPersonality] = useState("strict");
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [showSessions, setShowSessions] = useState(false);
  const bottomRef = useRef(null);

  // ---- Voice state -----------------------------------------------------
  const [voiceEnabled, setVoiceEnabled] = useState(() =>
    window.localStorage.getItem("cfo_voice_enabled") === "1",
  );
  const [selectedVoice, setSelectedVoice] = useState(() =>
    window.localStorage.getItem("cfo_voice_id") || "female",
  );
  const [voiceCatalog, setVoiceCatalog] = useState([]);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [ttsBusy, setTtsBusy] = useState(false);
  const [voiceMenuOpen, setVoiceMenuOpen] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioRef = useRef(null);

  useEffect(() => {
    window.localStorage.setItem("cfo_voice_enabled", voiceEnabled ? "1" : "0");
  }, [voiceEnabled]);
  useEffect(() => {
    window.localStorage.setItem("cfo_voice_id", selectedVoice);
  }, [selectedVoice]);
  useEffect(() => {
    if (!open) return;
    api.get("/finance/voice/voices").then(({ data }) => setVoiceCatalog(data.items)).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: "smooth" });
  }, [messages, sending]);

  useEffect(() => {
    if (open) loadSessions();
  }, [open]);

  // When opened with an initialSessionId (e.g. from VoiceQuickResponse's
  // "Ver en chat" action), pre-load that conversation.
  useEffect(() => {
    if (open && initialSessionId) {
      openSession(initialSessionId);
    }
  }, [open, initialSessionId]);

  const loadSessions = async () => {
    try {
      const { data } = await api.get("/finance/chat/sessions");
      setSessions(data);
    } catch (err) {
      /* silent */
    }
  };

  const openSession = async (sid) => {
    try {
      const { data } = await api.get(`/finance/chat/messages/${sid}`);
      setMessages(data);
      setSessionId(sid);
      setShowSessions(false);
    } catch (err) {
      toast.error("No se pudo abrir la conversación");
    }
  };

  const newChat = () => {
    setSessionId(null);
    setMessages([]);
    setInput("");
    setShowSessions(false);
  };

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setSending(true);
    setInput("");
    // Optimistic user message
    setMessages((prev) => [
      ...prev,
      { role: "user", content: msg, message_id: `tmp_${Date.now()}` },
    ]);
    try {
      const { data } = await api.post("/finance/chat", {
        message: msg,
        session_id: sessionId,
        personality,
        scope: scope === "all" ? "all" : scope,
        language: "es",
      });
      setSessionId(data.session_id);
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: data.reply, message_id: data.message_id },
      ]);
      loadSessions();
      // Read the reply aloud when voice output is enabled
      speakAnswer(data.reply);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo enviar");
      setMessages((prev) => prev.slice(0, -1)); // rollback optimistic
      setInput(msg);
    } finally {
      setSending(false);
    }
  };

  const deleteSession = async (sid) => {
    if (!window.confirm("¿Eliminar esta conversación?")) return;
    try {
      await api.delete(`/finance/chat/sessions/${sid}`);
      setSessions((prev) => prev.filter((s) => s.session_id !== sid));
      if (sid === sessionId) newChat();
      toast.success("Conversación eliminada");
    } catch (err) {
      toast.error("No se pudo eliminar");
    }
  };

  // -------------------- Voice input (Whisper STT) --------------------
  const startRecording = async () => {
    if (recording) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : "audio/webm";
      const mr = new MediaRecorder(stream, { mimeType: mime });
      audioChunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size && audioChunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: mime });
        if (blob.size < 1000) { toast.info("Grabación muy corta"); return; }
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, "voice.webm");
          fd.append("language", "es");
          const { data } = await api.post("/finance/voice/transcribe", fd);
          if (data.text?.trim()) {
            setInput((prev) => (prev ? `${prev} ${data.text}` : data.text).trim());
            // Auto-send after transcription
            setTimeout(() => send(data.text), 200);
          } else {
            toast.info("No se pudo entender");
          }
        } catch (err) {
          toast.error(err?.response?.data?.detail || "Fallo la transcripción");
        } finally {
          setTranscribing(false);
        }
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setRecording(true);
    } catch (err) {
      toast.error("Permiso de micrófono denegado o no disponible");
    }
  };
  const stopRecording = () => {
    if (!recording || !mediaRecorderRef.current) return;
    mediaRecorderRef.current.stop();
    setRecording(false);
  };

  // -------------------- Voice output (ElevenLabs TTS) --------------------
  const speakAnswer = async (text) => {
    if (!voiceEnabled || !text) return;
    // Trim to first 800 chars — the CFO answers can be long; save credits.
    const clean = text.replace(/[*_`#>]/g, "").replace(/\s+/g, " ").trim().slice(0, 800);
    setTtsBusy(true);
    try {
      const r = await fetch(`${API}/finance/voice/speak`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: clean, voice: selectedVoice, response_format: "audio" }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || "No se pudo leer");
        return;
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = "";
      }
      const a = new Audio(url);
      audioRef.current = a;
      a.onended = () => URL.revokeObjectURL(url);
      await a.play();
    } catch (err) {
      /* silent — voice is nice-to-have */
    } finally {
      setTtsBusy(false);
    }
  };

  const stopSpeaking = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setTtsBusy(false);
  };

  if (!open) return null;

  const activePersonality = PERSONALITIES.find((p) => p.key === personality) || PERSONALITIES[0];

  return (
    <div
      data-testid="cfo-chat-panel"
      className="fixed inset-y-0 right-0 z-40 w-full sm:w-[440px] md:w-[500px] bg-[#0F1017] border-l border-white/10 shadow-2xl flex flex-col"
      style={{ animation: "slideInRight 240ms cubic-bezier(0.16, 1, 0.3, 1)" }}
    >
      {/* Header */}
      <div className="p-4 border-b border-white/8 flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center deck-tile">
          <Robot size={20} weight="fill" className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-emerald-400">
            PERSONAL CFO · CLAUDE SONNET 4.6
          </div>
          <div className="text-base font-heading font-bold text-white truncate">
            Pregunta a tu dinero
          </div>
        </div>
        <button
          data-testid="chat-history-btn"
          onClick={() => setShowSessions((v) => !v)}
          title="Historial"
          className="w-9 h-9 rounded-lg hover:bg-white/10 text-white/60 hover:text-white flex items-center justify-center"
        >
          <ChatCircleText size={16} weight="bold" />
        </button>
        <div className="relative">
          <button
            data-testid="chat-voice-toggle"
            onClick={() => {
              if (voiceEnabled) stopSpeaking();
              setVoiceEnabled((v) => !v);
            }}
            title={voiceEnabled ? "Silenciar respuestas" : "Escuchar respuestas"}
            className={`w-9 h-9 rounded-lg flex items-center justify-center transition ${
              voiceEnabled
                ? "bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30"
                : "hover:bg-white/10 text-white/50 hover:text-white"
            }`}
          >
            {voiceEnabled
              ? <SpeakerHigh size={16} weight={ttsBusy ? "fill" : "bold"} />
              : <SpeakerSlash size={16} weight="bold" />}
          </button>
          {voiceEnabled && (
            <button
              data-testid="chat-voice-menu"
              onClick={() => setVoiceMenuOpen((v) => !v)}
              className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-emerald-500 text-slate-900 flex items-center justify-center"
              title="Cambiar voz"
            >
              <CaretDown size={9} weight="bold" />
            </button>
          )}
          {voiceMenuOpen && voiceEnabled && (
            <div
              data-testid="chat-voice-menu-popup"
              className="absolute right-0 top-11 z-50 w-52 rounded-lg border border-white/10 bg-[#12131C] shadow-2xl p-1.5"
            >
              {voiceCatalog.map((v) => (
                <button
                  key={v.key}
                  data-testid={`chat-voice-opt-${v.key}`}
                  onClick={() => { setSelectedVoice(v.key); setVoiceMenuOpen(false); }}
                  className={`w-full text-left px-2 py-2 rounded text-[11px] font-mono transition ${
                    selectedVoice === v.key
                      ? "on-solid bg-emerald-700 text-white"
                      : "text-white/70 hover:bg-white/6"
                  }`}
                >
                  {v.label}
                </button>
              ))}
            </div>
          )}
        </div>
        <button
          data-testid="chat-close-btn"
          onClick={onClose}
          className="w-9 h-9 rounded-lg hover:bg-white/10 text-white/60 hover:text-white flex items-center justify-center"
        >
          <X size={16} weight="bold" />
        </button>
      </div>

      {/* Personality selector */}
      <div className="px-4 py-3 border-b border-white/6 bg-black/30">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-white/40">
            Personalidad del CFO
          </span>
        </div>
        <div className="grid grid-cols-4 gap-1.5">
          {PERSONALITIES.map((p) => {
            const active = p.key === personality;
            return (
              <button
                key={p.key}
                data-testid={`chat-persona-${p.key}`}
                onClick={() => setPersonality(p.key)}
                title={p.description}
                className={`px-2 py-2 rounded-lg text-[10px] font-mono uppercase tracking-widest font-bold transition ${
                  active
                    ? "bg-emerald-500/20 border border-emerald-500/40 text-emerald-300"
                    : "bg-black/40 border border-white/8 text-white/50 hover:text-white/80"
                }`}
              >
                <div className="text-base leading-none mb-1">{p.emoji}</div>
                {p.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Sessions drawer */}
      {showSessions && (
        <div
          data-testid="chat-sessions-drawer"
          className="absolute top-[76px] right-4 w-72 max-h-96 overflow-y-auto rounded-lg bg-[#0F1017] border border-white/12 shadow-2xl z-10"
        >
          <div className="p-3 border-b border-white/8 flex items-center justify-between">
            <span className="text-[10px] font-mono uppercase tracking-widest text-white/50">Historial</span>
            <button
              onClick={newChat}
              className="text-[10px] font-mono text-emerald-400 hover:text-emerald-300 uppercase tracking-widest"
            >
              + Nuevo
            </button>
          </div>
          {sessions.length === 0 && (
            <div className="p-4 text-center text-[11px] font-mono text-white/30">
              Aún no tienes conversaciones.
            </div>
          )}
          {sessions.map((s) => (
            <div
              key={s.session_id}
              className={`p-3 border-b border-white/6 hover:bg-white/5 flex items-center gap-2 cursor-pointer group ${
                s.session_id === sessionId ? "bg-emerald-500/10" : ""
              }`}
              onClick={() => openSession(s.session_id)}
            >
              <div className="flex-1 min-w-0">
                <div className="text-[12px] font-heading font-semibold text-white truncate">
                  {s.title || "Conversación"}
                </div>
                <div className="text-[10px] font-mono text-white/40">
                  {new Date(s.touched_at || s.created_at).toLocaleString()}
                </div>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); deleteSession(s.session_id); }}
                className="w-6 h-6 rounded hover:bg-red-500/15 text-white/30 hover:text-red-400 flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
              >
                <Trash size={11} weight="bold" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 && (
          <div className="text-center py-8">
            <div className="inline-flex w-16 h-16 rounded-2xl bg-emerald-500/10 border border-emerald-500/25 items-center justify-center mb-3">
              <Sparkle size={26} weight="fill" className="text-emerald-400" />
            </div>
            <div className="text-base font-heading font-bold text-white mb-1">
              ¡Hola! Soy tu CFO {activePersonality.emoji}
            </div>
            <div className="text-[12px] font-mono text-white/50 mb-4">
              Ya vi tu balance, deudas e ingresos. Pregúntame lo que quieras.
            </div>
            <div className="grid grid-cols-1 gap-2">
              {QUICK_PROMPTS.map((q, i) => (
                <button
                  key={i}
                  data-testid={`chat-quick-${i}`}
                  onClick={() => send(q)}
                  className="px-3 py-2 rounded-lg bg-black/40 border border-white/8 hover:border-emerald-500/40 hover:bg-emerald-500/5 text-left text-[12px] font-mono text-white/70 hover:text-white transition"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          <div
            key={m.message_id || i}
            className={`flex gap-2 ${m.role === "user" ? "flex-row-reverse" : ""}`}
          >
            <div
              className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                m.role === "user"
                  ? "bg-[#2563EB]"
                  : "bg-gradient-to-br from-emerald-500 to-green-600"
              }`}
            >
              {m.role === "user" ? (
                <User size={14} weight="fill" className="text-white" />
              ) : (
                <Robot size={14} weight="fill" className="text-white" />
              )}
            </div>
            <div
              data-testid={`chat-msg-${m.role}-${i}`}
              className={`max-w-[85%] rounded-2xl px-4 py-2.5 ${
                m.role === "user"
                  ? "bg-[#2563EB] text-white"
                  : "bg-white/6 text-white/90 border border-white/8"
              }`}
            >
              <div className="text-[13px] leading-relaxed whitespace-pre-wrap">
                {m.content}
              </div>
            </div>
          </div>
        ))}

        {sending && (
          <div className="flex gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center shrink-0">
              <Robot size={14} weight="fill" className="text-white" />
            </div>
            <div className="bg-white/6 border border-white/8 rounded-2xl px-4 py-3 flex items-center gap-2">
              <div className="flex gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" style={{ animationDelay: "0ms" }} />
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" style={{ animationDelay: "200ms" }} />
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" style={{ animationDelay: "400ms" }} />
              </div>
              <span className="text-[11px] font-mono text-white/50">pensando...</span>
            </div>
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* Composer */}
      <div className="p-3 border-t border-white/8 bg-black/30">
        <form
          onSubmit={(e) => { e.preventDefault(); send(); }}
          className="flex items-end gap-2"
        >
          <textarea
            data-testid="chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            placeholder={`Escribe (${activePersonality.label})...`}
            rows={2}
            className="flex-1 resize-none bg-black/40 border border-white/10 focus:border-emerald-500/60 rounded-lg px-3 py-2 text-sm text-white font-mono outline-none placeholder-white/30"
          />
          <button
            data-testid="chat-mic-btn"
            type="button"
            onMouseDown={startRecording}
            onMouseUp={stopRecording}
            onMouseLeave={() => recording && stopRecording()}
            onTouchStart={startRecording}
            onTouchEnd={stopRecording}
            disabled={sending || transcribing}
            title={recording ? "Suelta para transcribir" : "Mantén presionado para hablar"}
            className={`w-11 h-11 rounded-lg flex items-center justify-center transition shrink-0 ${
              recording
                ? "bg-red-500 text-white animate-pulse ring-4 ring-red-500/30"
                : transcribing
                ? "bg-white/6 text-emerald-300"
                : "bg-white/6 hover:bg-emerald-500/30 text-white/60 hover:text-emerald-300"
            }`}
          >
            {transcribing ? (
              <ArrowClockwise size={16} weight="bold" className="animate-spin" />
            ) : recording ? (
              <Stop size={16} weight="fill" />
            ) : (
              <Microphone size={16} weight="bold" />
            )}
          </button>
          <button
            data-testid="chat-send-btn"
            type="submit"
            disabled={sending || !input.trim()}
            className="w-11 h-11 rounded-lg bg-emerald-500 hover:bg-emerald-600 disabled:opacity-40 disabled:cursor-not-allowed text-white flex items-center justify-center transition shrink-0"
          >
            {sending ? (
              <ArrowClockwise size={16} weight="bold" className="animate-spin" />
            ) : (
              <PaperPlaneRight size={16} weight="fill" />
            )}
          </button>
        </form>
        <div className="mt-1.5 flex items-center justify-between text-[10px] font-mono text-white/30">
          <span>
            {recording
              ? "🔴 Grabando... suelta para enviar"
              : transcribing
              ? "Transcribiendo..."
              : ttsBusy
              ? "🔊 Leyendo respuesta..."
              : "Enter = enviar · Micrófono = hablar"}
          </span>
          <span>{scope === "all" ? "TODOS" : scope === "personal" ? "PERSONAL" : "NEGOCIO"}</span>
        </div>
      </div>

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to   { transform: translateX(0);    opacity: 1; }
        }
      `}</style>
    </div>
  );
}
